<section>
  <app-admin-page-header>
    <p description>
      Content for the public homepage (<a routerLink="/">/</a>). Sections follow the same order as on the live page.
    </p>
    <ng-container actions>
      <app-admin-button variant="secondary" type="button" (click)="load()" [disabled]="loading()">Reload</app-admin-button>
      @if (showEdit()) {
        <app-admin-button variant="accent" type="button" (click)="save()" [disabled]="!canEdit() || saving() || loading()">
          {{ saving() ? 'Saving…' : 'Save' }}
        </app-admin-button>
      }
    </ng-container>
  </app-admin-page-header>

  @if (loadError(); as le) {
    <div class="cg-admin-alert cg-admin-alert--warning">{{ le }}</div>
  }
  @if (error(); as err) {
    <div class="cg-admin-alert cg-admin-alert--error">{{ err }}</div>
  }

  @if (loading()) {
    <div class="cg-admin-grid">
      @for (i of [1, 2, 3, 4, 5]; track i) {
        <app-admin-skeleton variant="card" />
      }
    </div>
  } @else {
    <div class="cg-admin-grid">
      <!-- 1. Announcement bar -->
      <app-admin-card title="Announcement bar" icon="megaphone">
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Pill</span>
            <input class="cg-input" [(ngModel)]="form.announcementPill" name="announcementPill" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">CTA label</span>
            <input class="cg-input" [(ngModel)]="form.announcementCtaLabel" name="announcementCtaLabel" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Text</span>
            <input class="cg-input" [(ngModel)]="form.announcementText" name="announcementText" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">CTA href</span>
            <input class="cg-input" [(ngModel)]="form.announcementCtaHref" name="announcementCtaHref" />
          </label>
        </div>
      </app-admin-card>

      <!-- 2. Popup -->
      <app-admin-card title="Popup" icon="image">
        <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
          Optional image popup on the public homepage. Shown once per visit after the delay below.
        </p>
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Enabled</span>
            <label class="cg-admin-toggle">
              <input type="checkbox" [(ngModel)]="form.popupEnabled" name="popupEnabled" />
              <span class="cg-admin-toggle__track"></span>
            </label>
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Delay (seconds)</span>
            <input
              class="cg-input"
              type="number"
              min="0"
              max="300"
              step="1"
              [(ngModel)]="form.popupDelaySeconds"
              name="popupDelaySeconds"
            />
          </label>
          <div class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Popup image</span>
            @if (popupPreviewUrl(); as previewUrl) {
              <div class="cg-admin-hero-bg-preview" style="max-width: 20rem">
                <img [src]="previewUrl" alt="Popup preview" />
              </div>
            }
            <div class="cg-admin-form-grid cg-admin-form-grid--2" style="align-items: end">
              <label class="cg-admin-field">
                <span class="cg-admin-field__label">Choose image</span>
                <input type="file" class="cg-input" accept="image/*" (change)="onPopupFile($event)" />
              </label>
              <div class="cg-admin-page-header__actions">
                @if (showUpload()) {
                  <app-admin-button
                    variant="accent"
                    type="button"
                    (click)="uploadPopupImage()"
                    [disabled]="!popupFile || popupUploading()"
                  >
                    {{ popupUploading() ? 'Uploading…' : 'Upload image' }}
                  </app-admin-button>
                }
                @if (form.popupImageMediaAssetId && canEdit()) {
                  <app-admin-button variant="secondary" type="button" (click)="clearPopupImage()">
                    Remove
                  </app-admin-button>
                }
              </div>
            </div>
            @if (popupUploadMessage(); as msg) {
              <p class="cg-form-help" style="margin-top: var(--cg-space-2)">{{ msg }}</p>
            }
          </div>
        </div>
      </app-admin-card>

      <!-- 3. Footer social links -->
      <app-admin-card title="Footer social links" icon="link">
        <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
          Shown in the footer across the public marketing site. Leave blank to hide an icon.
        </p>
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Instagram URL</span>
            <input class="cg-input" [(ngModel)]="form.socialInstagram" name="socialInstagram" placeholder="https://instagram.com/…" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Facebook URL</span>
            <input class="cg-input" [(ngModel)]="form.socialFacebook" name="socialFacebook" placeholder="https://facebook.com/…" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">YouTube URL</span>
            <input class="cg-input" [(ngModel)]="form.socialYoutube" name="socialYoutube" placeholder="https://youtube.com/…" />
          </label>
        </div>
      </app-admin-card>

      <!-- 4. Hero -->
      <app-admin-card title="Hero" icon="sparkles">
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Badge</span>
            <input class="cg-input" [(ngModel)]="form.heroBadge" name="heroBadge" />
          </label>
          <div class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Hero background image</span>
            <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
              Full-bleed texture behind the hero (like <code>/home1</code> world map). Saved automatically when you upload.
            </p>
            @if (heroBgPreviewUrl(); as previewUrl) {
              <div class="cg-admin-hero-bg-preview">
                <img [src]="previewUrl" alt="Hero background preview" />
              </div>
            }
            <div class="cg-admin-form-grid cg-admin-form-grid--2" style="align-items: end">
              <label class="cg-admin-field">
                <span class="cg-admin-field__label">Choose image</span>
                <input type="file" class="cg-input" accept="image/*" (change)="onHeroBackgroundFile($event)" />
              </label>
              <div class="cg-admin-page-header__actions">
                @if (showUpload()) {
                  <app-admin-button
                    variant="accent"
                    type="button"
                    (click)="uploadHeroBackground()"
                    [disabled]="!heroBgFile || heroBgUploading()"
                  >
                    {{ heroBgUploading() ? 'Uploading…' : 'Upload background' }}
                  </app-admin-button>
                }
                @if (form.heroMediaAssetId && canEdit()) {
                  <app-admin-button variant="secondary" type="button" (click)="clearHeroBackground()">Remove</app-admin-button>
                }
              </div>
            </div>
            @if (form.heroMediaAssetId) {
              <p class="cg-form-help" style="margin-top: var(--cg-space-2)">
                Asset id: <code class="cg-input--mono">{{ form.heroMediaAssetId }}</code>
              </p>
            }
            @if (heroBgUploadMessage(); as msg) {
              <p class="cg-form-help" style="margin-top: var(--cg-space-2)">{{ msg }}</p>
            }
          </div>
          <div class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Hero photo gallery</span>
            <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
              Images for the stacked hero card. Visitors can click the card to browse all photos in a full-screen gallery.
              Drag to reorder — the first image is the cover shown on the homepage.
            </p>

            @if (heroGalleryMessage(); as msg) {
              <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">{{ msg }}</p>
            }

            <article
              class="cg-admin-card cg-admin-hero-gallery"
              [class.cg-admin-hero-gallery--empty]="form.heroGallery.length === 0"
            >
              <div
                class="cg-admin-hero-gallery__strip"
                cdkDropList
                cdkDropListOrientation="horizontal"
                [cdkDropListData]="form.heroGallery"
                (cdkDropListDropped)="onHeroGalleryDrop($event)"
              >
                @for (item of form.heroGallery; track item.key; let i = $index) {
                  <div
                    class="cg-admin-hero-gallery__item"
                    [class.cg-admin-hero-gallery__item--cover]="i === 0"
                    cdkDrag
                  >
                    @if (heroGalleryPreview(item.key); as previewUrl) {
                      <div class="cg-admin-hero-gallery__thumb" cdkDragHandle>
                        <img [src]="previewUrl" [alt]="'Gallery image ' + (i + 1)" />
                        @if (i === 0) {
                          <span class="cg-admin-hero-gallery__cover-badge">Cover</span>
                        }
                      </div>
                    }
                    @if (showEdit()) {
                      <button
                        type="button"
                        class="cg-admin-hero-gallery__remove"
                        aria-label="Remove image"
                        (click)="removeHeroGalleryItem(item.key); $event.stopPropagation()"
                      >
                        &times;
                      </button>
                    }
                  </div>
                }

                @if (canAddHeroGalleryImages() && showUpload()) {
                  <button
                    type="button"
                    class="cg-admin-hero-gallery__add"
                    aria-label="Add gallery images"
                    [disabled]="heroGalleryUploading()"
                    (click)="heroGalleryFileInput.click()"
                  >
                    @if (heroGalleryUploading()) {
                      <span class="cg-admin-hero-gallery__add-label">Uploading…</span>
                    } @else {
                      <span class="cg-admin-hero-gallery__add-icon" aria-hidden="true">+</span>
                    }
                  </button>
                }
              </div>

              <input
                #heroGalleryFileInput
                type="file"
                class="cg-admin-hero-gallery__file-input"
                accept="image/*"
                multiple
                (change)="onHeroGalleryFiles($event)"
              />

              <p class="cg-form-help cg-admin-hero-gallery__meta">
                Drag to reorder · first image is the cover ·
                {{ form.heroGallery.length }} / {{ maxHeroGalleryImages }} images
              </p>
            </article>
          </div>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Floating badge value</span>
            <input class="cg-input" [(ngModel)]="form.heroSideBadgeValue" name="hsbv" placeholder="e.g. 12,000+" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Floating badge label</span>
            <input class="cg-input" [(ngModel)]="form.heroSideBadgeLabel" name="hsbl" placeholder="e.g. Students guided" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Title</span>
            <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
              One line per row. Wrap accent words in double braces, e.g.
              <code ngNonBindable>Education {{Journey}} Starts</code>.
            </p>
            <textarea
              class="cg-textarea cg-textarea--mono"
              rows="4"
              [(ngModel)]="form.heroTitle"
              name="heroTitle"
            ></textarea>
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Lede</span>
            <textarea class="cg-textarea" rows="2" [(ngModel)]="form.heroLede" name="heroLede"></textarea>
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Features (one per line)</span>
            <textarea class="cg-textarea cg-textarea--mono" rows="3" [(ngModel)]="form.heroFeaturesText" name="heroFeatures"></textarea>
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Primary CTA text</span>
            <input class="cg-input" [(ngModel)]="form.heroPrimaryCtaText" name="hpct" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Primary CTA anchor</span>
            <input class="cg-input" [(ngModel)]="form.heroPrimaryCtaAnchor" name="hpca" />
          </label>
        </div>
      </app-admin-card>

      <!-- 3. Stats strip -->
      <app-admin-card title="Stats strip">
        <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
          Shown below the hero. One stat per line: <code>value|label|icon</code> (icon optional).
        </p>
        <textarea class="cg-textarea cg-textarea--mono" rows="4" [(ngModel)]="form.statsText" name="statsText"></textarea>
      </app-admin-card>

      <!-- 4. Destinations -->
      <app-admin-card title="Destinations" icon="globe">
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <div class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">
              Featured countries
              <span class="cg-admin-field__hint">({{ featuredCountriesCount() }} / {{ maxFeaturedCountries }} selected)</span>
            </span>
            <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
              Choose up to {{ maxFeaturedCountries }} published countries for the homepage destinations grid. Order follows selection order.
            </p>
            @if (featuredCountriesMessage(); as msg) {
              <p class="cg-form-help cg-admin-field__hint--warn" style="margin-bottom: var(--cg-space-2)">{{ msg }}</p>
            }
            @if (countriesLoading()) {
              <div class="cg-admin-checkbox-list cg-admin-checkbox-list--loading">
                <app-admin-skeleton variant="text" />
                <app-admin-skeleton variant="text" />
                <app-admin-skeleton variant="text" />
              </div>
            } @else if (countriesForPicker().length === 0) {
              <p class="cg-form-help">
                No published countries yet.
                <a routerLink="/platform/countries/new">Create a country</a>
                and publish it to feature it here.
              </p>
            } @else {
              <ul class="cg-admin-checkbox-list" role="group" aria-label="Featured countries">
                @for (country of countriesForPicker(); track country.slug) {
                  <li>
                    <label
                      class="cg-admin-checkbox-item"
                      [class.cg-admin-checkbox-item--disabled]="!canToggleFeaturedCountry(country)"
                    >
                      <input
                        type="checkbox"
                        class="cg-admin-checkbox-item__input"
                        [checked]="isFeaturedCountry(country.slug)"
                        [disabled]="!canToggleFeaturedCountry(country)"
                        (change)="toggleFeaturedCountry(country.slug, $any($event.target).checked)"
                      />
                      <span class="cg-admin-checkbox-item__body">
                        <span class="cg-admin-checkbox-item__name">{{ country.name }}</span>
                        <span class="cg-admin-checkbox-item__slug">{{ country.slug }}</span>
                      </span>
                      @if (country.status !== 'PUBLISHED') {
                        <span class="cg-admin-checkbox-item__badge">Draft</span>
                      }
                    </label>
                  </li>
                }
              </ul>
            }
            @if (selectedFeaturedCountries().length > 0) {
              <div class="cg-admin-featured-country-cards">
                <p class="cg-form-help" style="margin: var(--cg-space-4) 0 var(--cg-space-3)">
                  Upload a portrait card image for each selected country (shown on the homepage grid).
                </p>
                @if (countryCardUploadMessage(); as cardMsg) {
                  <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">{{ cardMsg }}</p>
                }
                @for (country of selectedFeaturedCountries(); track country.slug) {
                  <div class="cg-admin-featured-country-card">
                    <div class="cg-admin-featured-country-card__head">
                      <span class="cg-admin-featured-country-card__name">{{ country.name }}</span>
                      <span class="cg-admin-featured-country-card__slug">{{ country.slug }}</span>
                    </div>
                    @if (countryCardPreview(country.slug); as previewUrl) {
                      <div class="cg-admin-featured-country-card__preview">
                        <img [src]="previewUrl" [alt]="country.name + ' card preview'" />
                      </div>
                    }
                    <div class="cg-admin-featured-country-card__actions">
                      <label class="cg-admin-field">
                        <span class="cg-admin-field__label">Card image</span>
                        <input
                          type="file"
                          class="cg-input"
                          accept="image/*"
                          (change)="onCountryCardFile(country.slug, $event)"
                        />
                      </label>
                      <div class="cg-admin-page-header__actions">
                        @if (showUpload()) {
                          <app-admin-button
                            variant="accent"
                            type="button"
                            (click)="uploadCountryCard(country.slug)"
                            [disabled]="!hasCountryCardFile(country.slug) || countryCardUploadingSlug() === country.slug"
                          >
                            {{ countryCardUploadingSlug() === country.slug ? 'Uploading…' : 'Upload' }}
                          </app-admin-button>
                        }
                        @if (form.featuredCountryCardMedia[country.slug] && canEdit()) {
                          <app-admin-button variant="secondary" type="button" (click)="clearCountryCard(country.slug)">
                            Remove
                          </app-admin-button>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Eyebrow</span>
            <input class="cg-input" [(ngModel)]="form.destinationsEyebrow" name="de" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Title</span>
            <input class="cg-input" [(ngModel)]="form.destinationsTitle" name="dt" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Subtitle</span>
            <input class="cg-input" [(ngModel)]="form.destinationsSubtitle" name="ds" />
          </label>
        </div>
      </app-admin-card>


      <!-- 5. Why study with us -->
      <app-admin-card title="Why study with us">
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Eyebrow</span>
            <input class="cg-input" [(ngModel)]="form.whyEyebrow" name="we" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Title</span>
            <input class="cg-input" [(ngModel)]="form.whyTitle" name="wt" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Subtitle</span>
            <input class="cg-input" [(ngModel)]="form.whySubtitle" name="ws" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Features (JSON array)</span>
            <textarea class="cg-textarea cg-textarea--mono" rows="4" [(ngModel)]="form.whyFeaturesJson" name="whyj"></textarea>
          </label>
        </div>
      </app-admin-card>

      <!-- 6. Testimonials -->
      <app-admin-card title="Testimonials" icon="message-square-quote">
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Testimonial ids (comma-separated)</span>
            <input class="cg-input cg-input--mono" [(ngModel)]="form.testimonialIdsCsv" name="tid" placeholder="Leave empty to show all published testimonials" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Section title</span>
            <input class="cg-input" [(ngModel)]="form.testimonialsTitle" name="tt" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Section subtitle</span>
            <input class="cg-input" [(ngModel)]="form.testimonialsSubtitle" name="tst" />
          </label>
        </div>
      </app-admin-card>

      <!-- 8. Partners -->
      <app-admin-card title="Partners">
        <div class="cg-admin-form-grid">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Title</span>
            <input class="cg-input" [(ngModel)]="form.partnersTitle" name="pt" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Partners JSON</span>
            <textarea class="cg-textarea cg-textarea--mono" rows="5" [(ngModel)]="form.partnersJson" name="pj"></textarea>
          </label>
        </div>
      </app-admin-card>

      <!-- 9. Consultation CTA -->
      <app-admin-card title="Consultation CTA">
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Eyebrow</span>
            <input class="cg-input" [(ngModel)]="form.ctaEyebrow" name="ctaEyebrow" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Title</span>
            <input class="cg-input" [(ngModel)]="form.ctaTitle" name="ctat" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Subtitle</span>
            <textarea class="cg-textarea" rows="2" [(ngModel)]="form.ctaSubtitle" name="ctas"></textarea>
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Bullets (comma-separated)</span>
            <input class="cg-input" [(ngModel)]="form.ctaBulletsCsv" name="ctab" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Form heading</span>
            <input class="cg-input" [(ngModel)]="form.ctaFormHeading" name="ctafh" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Legal line</span>
            <input class="cg-input" [(ngModel)]="form.ctaLegalLine" name="ctall" />
          </label>
        </div>
      </app-admin-card>

      <!-- 10. SEO (not visible on page — metadata only) -->
      <app-admin-card title="SEO" icon="search">
        <p class="cg-form-help" style="margin-bottom: var(--cg-space-3)">
          Page title, description, and social preview. Not shown on the homepage itself.
        </p>
        <div class="cg-admin-form-grid cg-admin-form-grid--2">
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Title</span>
            <input class="cg-input" [(ngModel)]="form.seoTitle" name="seoTitle" />
          </label>
          <label class="cg-admin-field cg-admin-field--full">
            <span class="cg-admin-field__label">Description</span>
            <textarea class="cg-textarea" rows="2" [(ngModel)]="form.seoDescription" name="seoDescription"></textarea>
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Keywords (comma-separated)</span>
            <input class="cg-input" [(ngModel)]="form.seoKeywordsCsv" name="seoKeywordsCsv" />
          </label>
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">OG image media asset id</span>
            <input class="cg-input cg-input--mono" [(ngModel)]="form.seoOgImageMediaAssetId" name="seoOg" />
          </label>
        </div>
      </app-admin-card>
    </div>

    <app-floating-action-bar>
      <p info class="cg-muted">Changes apply to the public homepage after save.</p>
      <ng-container actions>
        <app-admin-button variant="secondary" type="button" (click)="load()" [disabled]="loading()">Reload</app-admin-button>
        @if (showEdit()) {
          <app-admin-button variant="accent" type="button" (click)="save()" [disabled]="!canEdit() || saving() || loading()">
            {{ saving() ? 'Saving…' : 'Save' }}
          </app-admin-button>
        }
      </ng-container>
    </app-floating-action-bar>
  }
</section>
